import { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  getDeployInfo,
  checkRuntimeHealth,
  deployToJ2ee,
  type J2eeDeployInfo,
  type J2eeRuntimeHealth,
  type J2eeDeployResult,
} from '@/api/j2ee'

/**
 * Advanced → "Deploy to J2EE runtime". Packages the current project and pushes
 * it to an ALREADY-RUNNING WildFly runtime's admin deploy API (no restart —
 * the runtime rescans, schema-syncs, recompiles BL and hot-swaps). All the
 * parameters (runtime URL, admin token, target project name) are pre-filled
 * from server defaults and editable here.
 */
export default function J2eeDeployPage() {
  const { project } = useParams<{ project: string }>()
  const navigate = useNavigate()
  const projectId = project || ''

  const [info, setInfo] = useState<J2eeDeployInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Editable parameters
  const [runtimeUrl, setRuntimeUrl] = useState('')
  const [adminToken, setAdminToken] = useState('')
  const [targetName, setTargetName] = useState('')
  const [showToken, setShowToken] = useState(false)

  const [health, setHealth] = useState<J2eeRuntimeHealth | null>(null)
  const [testing, setTesting] = useState(false)
  const [deploying, setDeploying] = useState(false)
  const [result, setResult] = useState<J2eeDeployResult | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const data = await getDeployInfo(projectId)
      setInfo(data)
      setRuntimeUrl(data.defaults.runtime_url)
      setAdminToken(data.defaults.admin_token)
      setTargetName(data.defaults.target_name)
      setHealth(data.runtime)
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (e as Error)?.message ||
        'Failed to load deploy info'
      setLoadError(msg)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    if (projectId) load()
  }, [projectId, load])

  const onTest = async () => {
    setTesting(true)
    setHealth(null)
    try {
      const h = await checkRuntimeHealth(runtimeUrl)
      setHealth(h)
      if (h.reachable) toast.success('Runtime is reachable')
      else toast.error(h.detail || 'Runtime not reachable')
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Could not reach the runtime'
      setHealth({ reachable: false, detail: msg })
      toast.error(msg)
    } finally {
      setTesting(false)
    }
  }

  const onDeploy = async () => {
    if (!info?.eligible) return
    setDeploying(true)
    setResult(null)
    try {
      const r = await deployToJ2ee(projectId, {
        runtime_url: runtimeUrl,
        admin_token: adminToken,
        target_name: targetName,
      })
      setResult(r)
      toast.success(`Deployed to ${r.target_name}`)
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (e as Error)?.message ||
        'Deploy failed'
      toast.error(msg)
    } finally {
      setDeploying(false)
    }
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.8625rem',
    color: 'var(--color-text-secondary)',
    marginBottom: 4,
    fontWeight: 600,
  }
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.5rem 0.65rem',
    borderRadius: 6,
    border: '1px solid var(--color-border-strong)',
    background: 'var(--color-input-bg)',
    color: 'var(--color-text)',
    fontSize: '0.9625rem',
  }
  const row: React.CSSProperties = { marginBottom: '1rem' }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '1.5rem' }}>
      <button
        className="btn btn-secondary"
        onClick={() => navigate(`/workbench/${encodeURIComponent(projectId)}/advanced`)}
        style={{ marginBottom: '1rem' }}
      >
        <span className="material-icons" style={{ fontSize: 17, marginRight: 4 }}>
          arrow_back
        </span>
        Back to Advanced
      </button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.25rem' }}>
        <span className="material-icons" style={{ fontSize: 29, color: 'var(--color-accent)' }}>
          deployed_code
        </span>
        <h2 style={{ margin: 0, fontSize: '1.3625rem' }}>Deploy to J2EE runtime</h2>
      </div>
      <p style={{ margin: '0 0 1.25rem', color: 'var(--color-text-secondary)' }}>
        Package <strong>{projectId}</strong> and push it to a running WildFly runtime. The
        runtime rescans, syncs the schema, recompiles business logic and hot-swaps the app —
        no restart.
      </p>

      {loading && <p>Loading…</p>}
      {loadError && (
        <div className="card" style={{ borderColor: 'var(--color-danger, #c0392b)', marginBottom: '1rem' }}>
          <strong>Could not load:</strong> {loadError}
        </div>
      )}

      {info && !loading && (
        <>
          {/* Project facts */}
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ marginTop: 0, fontSize: '1.0625rem' }}>Project</h3>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.9625rem' }}>
              <span>
                <span style={{ color: 'var(--color-text-secondary)' }}>Database: </span>
                <strong>{info.db_mode}</strong>
              </span>
              <span>
                <span style={{ color: 'var(--color-text-secondary)' }}>Business logic: </span>
                <strong>{info.biz_logic}</strong>
              </span>
              {info.bl_dependencies.length > 0 && (
                <span>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Depends on: </span>
                  <strong>{info.bl_dependencies.join(', ')}</strong>
                </span>
              )}
            </div>
            {!info.eligible && (
              <div
                style={{
                  marginTop: '0.75rem',
                  padding: '0.6rem 0.75rem',
                  borderRadius: 6,
                  background: 'rgba(192,57,43,0.12)',
                  color: 'var(--color-danger, #c0392b)',
                  fontSize: '0.9125rem',
                }}
              >
                <strong>Not deployable to J2EE:</strong> {info.ineligible_reason}
              </div>
            )}
          </div>

          {/* Editable deployment parameters */}
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ marginTop: 0, fontSize: '1.0625rem' }}>Deployment settings</h3>

            <div style={row}>
              <label style={labelStyle}>Runtime URL (context root)</label>
              <input
                style={inputStyle}
                value={runtimeUrl}
                onChange={(e) => setRuntimeUrl(e.target.value)}
                placeholder="http://localhost:8180/ibase"
              />
            </div>

            <div style={row}>
              <label style={labelStyle}>Admin token</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  style={inputStyle}
                  type={showToken ? 'text' : 'password'}
                  value={adminToken}
                  onChange={(e) => setAdminToken(e.target.value)}
                  placeholder="RUNTIME_ADMIN_TOKEN"
                />
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => setShowToken((s) => !s)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {showToken ? 'Hide' : 'Show'}
                </button>
              </div>
              <small style={{ color: 'var(--color-text-secondary)' }}>
                Must match the runtime's <code>RUNTIME_ADMIN_TOKEN</code>.
              </small>
            </div>

            <div style={row}>
              <label style={labelStyle}>Deploy as (project name on runtime)</label>
              <input
                style={inputStyle}
                value={targetName}
                onChange={(e) => setTargetName(e.target.value)}
                placeholder={projectId}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn btn-secondary" onClick={onTest} disabled={testing}>
                {testing ? 'Testing…' : 'Test connection'}
              </button>
              {health && (
                <span style={{ fontSize: '0.9125rem' }}>
                  {health.reachable ? (
                    <span style={{ color: 'var(--color-success, #27ae60)' }}>
                      ● Reachable{typeof health.projects === 'number' ? ` — ${health.projects} project(s)` : ''}
                      {health.auth_mode ? `, auth: ${health.auth_mode}` : ''}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--color-danger, #c0392b)' }}>
                      ● Not reachable — {health.detail}
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>

          {/* Deploy action */}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              className="btn btn-primary"
              onClick={onDeploy}
              disabled={deploying || !info.eligible || !runtimeUrl || !adminToken || !targetName}
            >
              <span className="material-icons" style={{ fontSize: 19, marginRight: 6 }}>
                rocket_launch
              </span>
              {deploying ? 'Deploying…' : 'Deploy now'}
            </button>
            {!info.eligible && (
              <span style={{ fontSize: '0.9125rem', color: 'var(--color-text-secondary)' }}>
                Fix the eligibility issue above to enable deploy.
              </span>
            )}
          </div>

          {/* Result */}
          {result && (
            <div
              className="card"
              style={{ marginTop: '1.25rem', borderColor: 'var(--color-success, #27ae60)' }}
            >
              <h3 style={{ marginTop: 0, fontSize: '1.0625rem', color: 'var(--color-success, #27ae60)' }}>
                ✓ Deployed
              </h3>
              <p style={{ margin: '0 0 0.5rem', fontSize: '0.9625rem' }}>
                {result.summary.file_count} files ({(result.summary.bytes_compressed / 1024).toFixed(0)} KB),{' '}
                {result.summary.db_mode}/{result.summary.biz_logic} → <strong>{result.target_name}</strong>
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <a
                  href={result.app_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-secondary"
                  style={{ textDecoration: 'none' }}
                >
                  Open the app
                  <span className="material-icons" style={{ fontSize: 17, marginLeft: 4 }}>
                    open_in_new
                  </span>
                </a>
                {result.site_url && (
                  <a
                    href={result.site_url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-secondary"
                    style={{ textDecoration: 'none' }}
                  >
                    Open the website
                    <span className="material-icons" style={{ fontSize: 17, marginLeft: 4 }}>
                      open_in_new
                    </span>
                  </a>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
